import { BlobModel, FormFieldType, LoadingStateModel, type FormField, type MatrixValue } from '@/models';
import { type GraphFilter, type SurgeonDashboardData } from '@/models/surgeonDashboard';
import BlobService from '@/services/blobService';
import SurgeonDashboardService from '@/services/SurgeonDashboardService';
import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';


export const useSurgeonDashbordStore = defineStore('surgeon-dashboard', () => {
    
  const dashabordData = ref<SurgeonDashboardData>();
  const summaryTables = ref<FormField[]>([]);

  const isValidMatrixValue = (col: { id: string; value: unknown }) =>
    col.value !== undefined &&
    col.value !== '' &&
    col.value !== null &&
    col.value !== 'undefined';

  const isDashoboardLoading = reactive(new LoadingStateModel({isLoading: false}));
  const getSurgeonDashboardData = async (caseId: string) => {
    isDashoboardLoading.startLoading();
    dashabordData.value = undefined;
    try {
      const data = await SurgeonDashboardService.getSurgeonDashboardData(caseId);
      if (data) {
        // loading images asynchronously, instead of watiing for all images to load before setting the data
        const imageFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.FILE);
        imageFields.forEach(imageField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === imageField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });
        getFilesMetadata(imageFields);

        let matrixFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.MATRIX);
        // Remove all matrix fields from perfusionSummaryData
        matrixFields.forEach(matrixField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === matrixField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });

        //filter out matrixFields that have no valid values
        matrixFields = matrixFields.filter(summaryData => {
          const values = summaryData.value ? JSON.parse(summaryData.value) as MatrixValue[] : [];
          const finalValues = values.filter((row: MatrixValue) => row.values.some(isValidMatrixValue));
          return finalValues.length > 0;
        });
        summaryTables.value = matrixFields;
      }
      dashabordData.value = data;
    } catch (error) {
      isDashoboardLoading.setError(error);
    } finally {
      isDashoboardLoading.stopLoadingAndFinish();
    }
  };

  const interactiveDashabordData = ref<SurgeonDashboardData>();

  const getInteractiveSurgeonDashboardData = async (caseId: string, pumpFilters?: GraphFilter[]) => {
    isDashoboardLoading.startLoading();
    interactiveDashabordData.value = undefined;
    try {
      let postFilters: Record<string, string> | undefined = undefined;
      if (pumpFilters && pumpFilters.length > 0) {
        postFilters = {};
        pumpFilters?.forEach(filter => {
          postFilters![filter.key] = filter.value;
        });
      }
      const data: SurgeonDashboardData = await SurgeonDashboardService.getInteractiveSurgeonDashboardData(caseId, undefined, postFilters);
      if (data) {
        // loading images asynchronously, instead of watiing for all images to load before setting the data
        const imageFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.FILE);
        imageFields.forEach(imageField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === imageField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });
        getFilesMetadata(imageFields);

        let matrixFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.MATRIX);
        // Remove all matrix fields from perfusionSummaryData
        matrixFields.forEach(matrixField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === matrixField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });

        //filter out matrixFields that have no valid values
        matrixFields = matrixFields.filter(summaryData => {
          const values = summaryData.value ? JSON.parse(summaryData.value) as MatrixValue[] : [];
          const finalValues = values.filter((row: MatrixValue) => row.values.some(isValidMatrixValue));
          return finalValues.length > 0;
        });
        summaryTables.value = matrixFields;
      }
      interactiveDashabordData.value = data;
    } catch (error) {
      isDashoboardLoading.setError(error);
    } finally {
      isDashoboardLoading.stopLoadingAndFinish();
    }
  };

  const getInteractiveVariableData = async (caseId: string, field?: FormField, filters?: GraphFilter[]) => {
    try {
      const postFilters: Record<string, string> = {};
      filters?.forEach(filter => {
        postFilters[filter.key] = filter.value;
      });
      return await SurgeonDashboardService.getInteractiveSurgeonDashboardData(caseId, field?.variableName, postFilters);
      
    } catch (error) {
      isDashoboardLoading.setError(error);
    }
  };

  const summaryLiverImages = ref<FormField[]>([]);
  
  const getFilesMetadata = async (imageFields: FormField[]) => {
    for (const field of imageFields) {
      const files: BlobModel[] = [];
      if (field.fileIds) {
        // If fileIds is a string, parse it to an array
        for (const fileId of field.fileIds) {
          try {
            const file = await BlobService.getBlobMetadata(fileId);
            if (file) {
              files.push(file);
            }
          } catch (error) { /* empty */ }
        }
      } else if (field.images) {
        for (const image of field.images) {
          try {
            const file = await BlobService.getBlobMetadata(image.value);
            if (file) {
              file.label = image.label;
              files.push(file);
            }
          } catch (error) { /* empty */ }
        }
      }
      field.files = files;
    }
    summaryLiverImages.value = imageFields;
  };

  const loadFullResolutionImages = async (variableName: string) => {
    const imageField = summaryLiverImages.value.find(field => field.variableName === variableName);
    if (imageField) {
      for (const image of imageField.files ?? []) {
        image.downloadedBlob = await BlobService.downloadBlob(image.identifier, false, image.name, true);
      }
    }
  };

  const responsiveOptions = ref([
    {
      breakpoint: '1500px',
      numVisible: 5
    },
    {
      breakpoint: '1024px',
      numVisible: 3
    },
    {
      breakpoint: '768px',
      numVisible: 2
    },
    {
      breakpoint: '560px',
      numVisible: 1
    }
  ]);

  return {
    getSurgeonDashboardData,
    getInteractiveSurgeonDashboardData,
    getInteractiveVariableData,
    dashabordData,
    interactiveDashabordData,
    summaryLiverImages,
    summaryTables,
    responsiveOptions,
    loadFullResolutionImages,
    isDashoboardLoading
  };
});
